import React, { useState } from "react";
import {
  Loader,
  Modal,
  ModalParagraphText,
  StandardButton,
  SubmitButton,
} from "../../../components";
import { COLOURS } from "../../../shared";
import styled from "@emotion/styled";
import { CircleHelp } from "lucide-react";
import check from "../../../images/check.svg";
import log from "../../../shared/utils/log";

const RadioButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 3rem;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.emphasis};
  line-height: 2.4rem;

  input[type="radio"] {
    appearance: none;
    background-color: ${COLOURS.modalRadioButtonInputBackground};
    border: 1px solid ${COLOURS.modalRadioButtonInputBorder};
    border-radius: 0.6rem;
    padding: 0.6rem;
    display: inline-block;
    position: relative;
    width: 2rem;
    height: 2rem;

    &:checked {
      background-color: ${COLOURS.modalRadioCheckedButtonInputBackground};
      border-color: ${COLOURS.modalRadioCheckedButtonInputBorder};
      transition:
        background-color 0.2s,
        border-color 0.2s;
      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url(${check});
        background-size: 80% 80%;
        background-position: 50% 50%;
        background-repeat: no-repeat;
        transition: background-image 0.2s;
      }
    }
  }
`;

export const MatterStateSelectionModal: React.FC<{
  closeModal: () => void;
  onConfirm: (hasMattersInMultipleStates: boolean) => void;
  region: string;
}> = ({ closeModal, onConfirm, region }) => {
  const [selectedMatterState, setSelectedMatterState] = useState<
    boolean | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectOption = (value: boolean) => {
    log("the selected option is: ", value);
    setSelectedMatterState(value);
  };

  const handleGoBack = () => {
    log("Go back clicked");
    closeModal();
  };

  const handleSubmit = async () => {
    if (selectedMatterState === null) {
      console.error("No option selected for matter type states.");
      return;
    }
    setIsLoading(true);
    try {
      onConfirm(selectedMatterState);
    } catch (error) {
      console.error("Error during the process:", error);
    } finally {
      setIsLoading(false);
      closeModal();
    }
  };

  const isSubmitButtonDisabled = selectedMatterState === null;
  const additionalGBParagraphText =
    region === "GB" ? " (England/Scotland/Wales/Northern Ireland)." : ".";
  const stateOrCountry = region === "GB" ? "country" : "state";
  const statesOrCountries = region === "GB" ? "countries" : "states";

  return (
    <>
      <Modal
        backgroundColor={COLOURS.matterStateSelectionIconBackground}
        icon={
          <CircleHelp
            color={COLOURS.matterStateSelectionIconColor}
            strokeWidth={2}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        }
        buttons={[
          {
            Button: StandardButton,
            label: "Cancel, go back",
            action: handleGoBack,
          },
          {
            Button: ({ ...props }) => (
              <SubmitButton
                {...props}
                onClick={handleSubmit}
                disabled={isSubmitButtonDisabled}
                isSubmitButtonDisabled={isSubmitButtonDisabled}
              />
            ),
            label: "Continue",
            action: handleSubmit,
          },
        ]}
        headingText={`Are you importing matters for ${statesOrCountries} other than the Firm's ${stateOrCountry}?`}
      >
        <ModalParagraphText>
          This will determine the Smokeball Matter Types available for mapping
          in the following steps{additionalGBParagraphText}
          <RadioButtonContainer>
            <input
              type="radio"
              id="noAdditionalMatterStates"
              name="selectedMatterStatesOption"
              value="no"
              onChange={() => handleSelectOption(false)}
              style={{ cursor: "pointer", marginRight: "1rem" }}
            />
            <label htmlFor="noAdditionalMatterStates">
              <b style={{ color: COLOURS.standardBlackText }}>No</b>
              <br />
              I'm only importing matters for my Firm's {stateOrCountry}.
            </label>
          </RadioButtonContainer>
          <RadioButtonContainer>
            <input
              type="radio"
              id="yesAdditionalMatterStates"
              name="selectedMatterStatesOption"
              value="yes"
              onChange={() => handleSelectOption(true)}
              style={{ cursor: "pointer", marginRight: "1rem" }}
            />
            <label htmlFor="yesAdditionalMatterStates">
              <b style={{ color: COLOURS.standardBlackText }}>Yes</b>
              <br />
              I'm importing matters for {statesOrCountries} other than the
              Firm's {stateOrCountry}.
            </label>
          </RadioButtonContainer>
        </ModalParagraphText>
      </Modal>
      {isLoading && <Loader />}
    </>
  );
};
